/**
 * The kanban board: DndContext wrapping a row of <Column>s.
 *
 * On drop, calls `useMoveTask` with the destination column + the IDs of
 * the cards immediately before/after the drop point (so the server can
 * compute the right `position`).
 *
 * FRIDAY-75: the board can be sorted by issuer, due date or priority. The
 * cards inside a column follow that order; dropping a card in another
 * column still moves it there. The "Sort by" control lives in the project
 * header, so the board itself keeps its full height.
 */

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type Over,
  type PointerSensorOptions,
} from '@dnd-kit/core'
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import type { Task } from '@friday/shared/realtime'
import { Column } from './Column'
import { Card } from './Card'
import { useBoard, useMoveTask } from '@/features/projects/board'
import { useProfiles, pickDisplay } from '@/features/profiles/profiles'
import { sortTasks, type BoardSort, type SortContext } from '@/features/projects/sorting'

interface BoardProps {
  wsId: string
  pId: string
  filterSprintId?: string | null
  onOpenTask: (task: Task) => void
  selectedTaskId?: string | null
  /** The sort the board shows (FRIDAY-75). */
  sort: BoardSort
}

export function Board({ wsId, pId, filterSprintId, onOpenTask, selectedTaskId, sort }: BoardProps) {
  const board = useBoard(wsId, pId)
  const move = useMoveTask(wsId, pId)
  const [activeTask, setActiveTask] = useState<Task | null>(null)
  const sensors = useSensors(
    useSensor(MousePenSensor, { activationConstraint: { distance: 4 } }),
    // TouchSensor needs an intentional press so vertical scrolling still works;
    // once active it lets a touch drag reach a card in a full column.
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } }),
  )
  const scrollerRef = useRef<HTMLDivElement>(null)

  const allTasks = useMemo(() => board.data?.tasks ?? [], [board.data?.tasks])
  const visibleTasks = useMemo(
    () => (filterSprintId ? allTasks.filter((t) => t.sprintId === filterSprintId) : allTasks),
    [allTasks, filterSprintId],
  )
  // The assignees are people, so the board needs their display names and
  // avatars (FRIDAY-75). The profiles endpoint takes them in one batch.
  const assigneeIds = useMemo(
    () => [...new Set(visibleTasks.map((t) => t.assigneeUserId).filter((id): id is string => !!id))],
    [visibleTasks],
  )
  const { profiles } = useProfiles(assigneeIds)

  // Destination = the column under the pointer. Only column droppables
  // (whole column: header, cards, footer) are considered, so hovering a card
  // in a full column still resolves to that column. Pointer positions outside
  // the horizontally scrolling board viewport never hit a column clipped
  // beneath it.
  const collisionDetection: CollisionDetection = (args) => {
    const columns = args.droppableContainers.filter(
      (c) => (c.data.current as { type?: string } | undefined)?.type === 'column',
    )
    const p = args.pointerCoordinates
    if (!p) return rectIntersection({ ...args, droppableContainers: columns })
    const viewport = scrollerRef.current?.getBoundingClientRect()
    if (viewport && (p.x < viewport.left || p.x > viewport.right || p.y < viewport.top || p.y > viewport.bottom)) {
      return []
    }
    return pointerWithin({ ...args, droppableContainers: columns })
  }

  if (board.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-surface-500">
        Loading board…
      </div>
    )
  }
  if (board.error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">
        Failed to load board: {String((board.error as Error).message ?? board.error)}
      </div>
    )
  }
  if (!board.data) return null

  const columns = board.data.columns
  const sprints = board.data.sprints
  const milestones = board.data.milestones

  const sortContext: SortContext = {
    nameOf: (userId) => pickDisplay(userId, profiles[userId]).displayName,
    columnRank: (columnId) => columns.find((c) => c.id === columnId)?.position ?? 0,
    sprintName: (sprintId) =>
      sprintId ? (sprints.find((s) => s.id === sprintId)?.name ?? null) : null,
    milestoneName: (milestoneId) =>
      milestoneId ? (milestones.find((m) => m.id === milestoneId)?.name ?? null) : null,
  }

  const tasksByColumn = new Map<string, Task[]>()
  for (const col of columns) tasksByColumn.set(col.id, [])
  for (const t of visibleTasks) {
    const list = tasksByColumn.get(t.columnId)
    if (list) list.push(t)
  }
  for (const [columnId, list] of tasksByColumn) {
    tasksByColumn.set(
      columnId,
      sort.key === 'manual'
        ? [...list].sort((a, b) => a.position - b.position)
        : sortTasks(list, sort.key, sort.direction, sortContext),
    )
  }

  function onDragStart(e: DragStartEvent) {
    const t = (e.active.data.current as { task?: Task } | undefined)?.task
    if (t) setActiveTask(t)
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveTask(null)
    if (!e.over || !board.data) return
    const targetColumnId = resolveDropColumnId(e.over)
    if (!targetColumnId) return
    const taskId = String(e.active.id)

    // Find the source task and the indices of the cards now in the destination column.
    const task = board.data.tasks.find((t) => t.id === taskId)
    if (!task) return
    if (task.columnId === targetColumnId) return

    const destList = (tasksByColumn.get(targetColumnId) ?? []).filter((t) => t.id !== taskId)
    // Drop at end.
    const lastTask: Task | null = destList[destList.length - 1] ?? null
    move.mutate({
      taskId,
      columnId: targetColumnId,
      prevTaskId: lastTask?.id ?? null,
      nextTaskId: null,
      version: task.version,
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveTask(null)}
    >
      <div ref={scrollerRef} className="flex h-full gap-3 overflow-x-auto p-4">
        {board.data.columns.map((col) => (
          <Column
            key={col.id}
            wsId={wsId}
            pId={pId}
            column={col}
            tasks={tasksByColumn.get(col.id) ?? []}
            profiles={profiles}
            onOpenTask={onOpenTask}
            selectedTaskId={selectedTaskId}
          />
        ))}
      </div>
      <DragOverlay zIndex={1200} dropAnimation={null}>
        {activeTask && (
          <div className="w-72 rotate-2 cursor-grabbing">
            <Card task={activeTask} profiles={profiles} onOpen={() => {}} className="w-full shadow-xl" />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

/**
 * PointerSensor that ignores touch pointers. Touch is owned by the delayed
 * TouchSensor; otherwise a 4px swipe would start a drag instead of scrolling.
 */
class MousePenSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (event: ReactPointerEvent, options: PointerSensorOptions) =>
        event.nativeEvent.pointerType !== 'touch' &&
        PointerSensor.activators[0]!.handler(event, options),
    },
  ]
}

/**
 * Column ID for a drop target, read only from the target's own data: a
 * column droppable carries `columnId`; a sortable card carries its task.
 * Anything else (unknown droppable, bare ID) resolves to null.
 */
function resolveDropColumnId(over: Over): string | null {
  const data = over.data.current as { type?: string; columnId?: string; task?: Task } | undefined
  if (data?.type === 'column') return data.columnId ?? null
  if (data?.type === 'task') return data.task?.columnId ?? null
  return null
}
